/**
 * MoneyPage - live data only. /api/v1/dashboard/money.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { PageGuide } from "./PageGuide.v2";
import { useDashboardPage, formatINR, formatNum } from "./useDashboardPage.v2";
import { rangeWordFor, granLabel as glab, granWord as gword } from "./rangeWord.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

export function MoneyPage({ range, rangeId }) {
  const { data, loading, error, reload } = useDashboardPage("money", range, rangeId);

  if (loading) return <LoadingShell tiles={4} />;
  if (!data) return <EmptyError title="Couldn't load Money" error={error} onRetry={reload} />;

  const isTillDate = data.range?.isTillDate;
  const word = rangeWordFor(rangeId);
  const windowWord = word; // alias for places that previously used "lifetime/in window"
  const fullINR = (n) => "Rs " + Math.round(n || 0).toLocaleString("en-IN");

  // All hero tiles follow the date pill — every number on this row reflects
  // the selected window so the page is internally consistent. Defensive
  // guards: if the backend response shape ever drifts (e.g. safe() returns
  // an empty fallback) the page must not hard-crash — render the error
  // banner instead.
  const hero   = data.hero || {};
  const rev    = hero.revenueWindow || {};
  const txns   = hero.transactionsWindow || {};
  const netW   = hero.netWindow || {};
  const avgDay = hero.avgPerDayWindow || {};
  const days   = data.range?.daysInWindow || 1;

  const heroTiles = [
    {
      label: `Total inflow · ${windowWord}`, icon: "T", variant: "muted-orange",
      value: formatINR(rev.value),
      subValue: `Fan ${formatINR(rev.fan)} + Brand ${formatINR(rev.brand)}`,
      title: `Total inflow (${windowWord}):\n  Call revenue: ${fullINR(rev.fan)}\n  Brand inflow: ${fullINR(rev.brand)}\n  Total:        ${fullINR(rev.value)}`,
    },
    {
      label: `Transactions · ${windowWord}`, icon: "N", variant: "muted-violet",
      value: formatNum(txns.value),
      subValue: `Fan ${formatNum(txns.fan)} + Brand ${formatNum(txns.brand)}`,
      title: `Successful transactions (${windowWord}):\n  Fan-side:   ${formatNum(txns.fan)}\n  Brand-side: ${formatNum(txns.brand)}\n  Total:      ${formatNum(txns.value)}`,
    },
    {
      label: `Net (after GST) · ${windowWord}`, icon: "Ne", variant: "muted-lime",
      value: formatINR(netW.value),
      subValue: "gross ÷ 1.18",
      title: `Net revenue (after 18% GST):\n  Gross: ${fullINR(rev.value)}\n  GST:   ${fullINR((rev.value * 18 / 118))}\n  Net:   ${fullINR(netW.value)}`,
    },
    {
      label: `Avg per day · ${windowWord}`, icon: "D", variant: "muted-magenta",
      value: formatINR(avgDay.value),
      subValue: `${formatNum(days)} ${days === 1 ? 'day' : 'days'} in window`,
      title: `Average daily revenue:\n  Window: ${formatNum(days)} ${days === 1 ? 'day' : 'days'}\n  Total:  ${fullINR(rev.value)}\n  Avg:    ${fullINR(avgDay.value)} / day`,
    },
  ];
  const cb  = data.wallets.creatorBreakdown || {};
  const ff  = data.wallets.fanFlow      || {};
  const bf  = data.wallets.brandFlow    || {};
  const cpd = data.wallets.creatorPotDetail || {};
  const cCalling = cpd.calling  || {};
  const cBrand   = cpd.brand    || {};
  const cReferral= cpd.referral || {};

  // Fan flow tile — CEO equation 2026-05-06:
  //   Received + Admin − Creator-paid − Expired = Available
  //
  //   Received    = real cash fans paid Fancall via Cashfree-able rails
  //                 (wallet top-ups + direct call-booking payments)
  //   Admin       = operator-issued credits (promo, yt-referral, etc.)
  //   Creator-paid = money fans paid to creators (wallet-debit + direct UPI)
  //   Expired     = derived residual; promo credits zeroed silently
  //   Available   = current fan-wallet balance
  // Refunds intentionally excluded from this view per CEO directive.
  const fanTip =
    `Fan flow (${word}; available is right now):\n` +
    `  Received from fans:   + ${fullINR(ff.received)}\n` +
    `    (wallet top-ups + direct call payments via UPI/card)\n` +
    `  Admin/promo credit:   + ${fullINR(ff.admin)}\n` +
    `  Paid to creators:     − ${fullINR(ff.creatorEarn)}\n` +
    (ff.expired > 0
      ? `  Expired / reversed:   − ${fullINR(ff.expired)}\n` +
        `    (promo credits zeroed silently by the platform)\n`
      : '') +
    `  ──────────────────────────\n` +
    `  Available now:          ${fullINR(ff.available)}\n\n` +
    `  Math balances by construction. "Received" should reconcile\n` +
    `  with Cashfree settlement totals for the same window.`;

  // Creator tile — three wallets, shown as breakdown chips so nothing is cut off.
  // The big number is the SUM of current available across all three pots.
  // Per-pot Earned / Withdrawn / Available are surfaced in a dedicated section
  // below so the user can see the full flow story (not just current balance).
  const creatorTip =
    `Creator wallets (three-wallet model — current available):\n` +
    `  Calling wallet:  ${fullINR(cb.calling)}\n` +
    `  Referral wallet: ${fullINR(cb.referral)}\n` +
    `  Brand wallet:    ${fullINR(cb.brand)}\n` +
    `  Total:           ${fullINR((cb.calling || 0) + (cb.referral || 0) + (cb.brand || 0))}\n\n` +
    `  See "Creator wallet detail" below for lifetime earned + withdrawn.`;

  // Brand tile — sourced directly from brand_wallet.balance + spent columns.
  // Brand tile — Added is now REAL online deposits only (payment_mode='online').
  // Internal/admin deposits (payment_mode='wallet' or null) shown separately
  // — same reasoning as the fan-side admin-credit split.
  const brandTip =
    `Brand wallet (${word} flows; available is current):\n` +
    `  Added — online:      ${fullINR(bf.added)}\n` +
    (bf.addedAdmin > 0
      ? `  Internal/admin:      ${fullINR(bf.addedAdmin)}  (payment_mode=wallet, not real cash)\n`
      : '') +
    `  Used (spent):      − ${fullINR(bf.used)}\n` +
    `  ─────────────────\n` +
    `  Available now:       ${fullINR(bf.available)}\n\n` +
    `  "Added" is filtered to payment_mode='online' so it matches\n` +
    `  what actually settled into Fancall's bank account.`;

  const walletTiles = [
    {
      label: "Fan wallets", icon: "F", variant: "muted-orange",
      value: formatINR(data.wallets.fan),
      subValue: `current fan balance · ${word}`,
      title: fanTip,
      breakdown: [
        { label: "Added to wallet",    value: formatINR(ff.received) },
        { label: "Earn from referral", value: formatINR(ff.referral) },
        { label: "Added by admin",     value: formatINR(ff.admin) },
        { label: "Used in calling",    value: formatINR(ff.creatorEarn) },
        ...(ff.expired > 0 ? [{ label: "Expired promo", value: formatINR(ff.expired) }] : []),
        { label: "Available",          value: formatINR(ff.available) },
      ],
    },
    {
      label: "Creator wallets", icon: "C", variant: "muted-lime",
      value: formatINR(data.wallets.creator),
      subValue: "earned / withdrawn / available · by pot",
      title: creatorTip,
      breakdown: [
        // Every pot now reads its REAL ledger so the card cross-foots
        // (earned ≈ withdrawn + available per pot):
        //   • calling  earned = earnings table (NaN-guarded, non-referral)
        //   • referral earned = transaction_history (yt-referral-reward)
        //   • brand    earned = brand_transaction_history (campaign_earning)
        // Withdrawn call+ref is combined — both come from the one payout table
        // (split columns), so there's no double-count.
        { label: "Earn — calling",       value: formatINR(cpd.calling?.earned) },
        { label: "Earn — referral",      value: formatINR(cpd.referral?.earned) },
        { label: "Earn — brand",         value: formatINR(cpd.brand?.earned) },
        { label: "Withdrawn — call/ref", value: formatINR((cpd.calling?.withdrawn || 0) + (cpd.referral?.withdrawn || 0)) },
        { label: "Withdrawn — brand",    value: formatINR(cpd.brand?.withdrawn) },
        { label: "Avail — call/ref",     value: formatINR((cpd.calling?.available || 0) + (cpd.referral?.available || 0)) },
        { label: "Avail — brand",        value: formatINR(cpd.brand?.available) },
      ],
    },
    {
      label: "Brand wallets", icon: "B", variant: "muted-magenta",
      value: formatINR(data.wallets.brand),
      subValue: `current brand balance · ${word}`,
      title: brandTip,
      breakdown: [
        { label: "Added to wallet",  value: formatINR(bf.added) },
        ...(bf.addedAdmin > 0 ? [{ label: "Admin added", value: formatINR(bf.addedAdmin) }] : []),
        { label: "Used in campaign", value: formatINR(bf.used) },
        { label: "Available",        value: formatINR(bf.available) },
      ],
    },
    {
      label: "Total liability", icon: "L", variant: "muted-violet",
      value: formatINR(data.wallets.totalLiability),
      subValue: "current · fan + creator + brand",
    },
  ];
  const operationsTiles = [
    {
      label: "Pending payouts", icon: "P", variant: "muted-violet",
      value: formatINR(data.payouts.pendingTotal),
      subValue: data.payouts.pendingCount > 0
        ? `${formatNum(data.payouts.pendingCount)} requests queued (calls ${formatINR(data.payouts.callPendingTotal)} + brand ${formatINR(data.payouts.brandPendingTotal)})`
        : "no payouts queued",
      title: `Pending payouts (${word} · created in range)\n  Call wallet:  ${fullINR(data.payouts.callPendingTotal)}  (${formatNum(data.payouts.callPendingCount)} req)\n  Brand wallet: ${fullINR(data.payouts.brandPendingTotal)}  (${formatNum(data.payouts.brandPendingCount)} req)`,
    },
    {
      label: `GST · ${windowWord}`, icon: "G", variant: "muted-lime",
      value: formatINR(data.gst.collectedWindow),
      subValue: `Fan ${formatINR(data.gst.fanGstWindow)} + Brand ${formatINR(data.gst.brandGstWindow)}`,
      title: `GST collected (${windowWord})\n  Fan rail:   ${fullINR(data.gst.fanGstWindow)}\n  Brand rail: ${fullINR(data.gst.brandGstWindow)}`,
    },
    {
      label: `Refunds · ${windowWord}`, icon: "R", variant: "muted-magenta",
      value: formatINR(data.refunds.windowAmount),
      subValue: data.refunds.windowCount > 0
        ? `${formatNum(data.refunds.windowCount)} approved tickets · fan-initiated only`
        : "no approved refunds in window",
    },
  ];

  const trend = data.revenueTrendLast12Months || [];
  const latest = trend[trend.length - 1]?.value || 0;
  const prev   = trend[trend.length - 2]?.value || 0;
  const delta  = prev > 0 ? ((latest - prev) / prev) * 100 : 0;

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>Money</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Inflow (call revenue + brand), payouts, GST, refunds, wallet liabilities · live · {word}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {heroTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {trend.length > 0 && (() => {
        const gran = data.range?.granularity || 'month';
        const granLabel = glab(gran);
        const granWord  = gword(gran);
        const titleSuffix = isTillDate ? 'last 12 months' : word;
        return (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, padding: "0 4px" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Total inflow trend — {titleSuffix}</div>
                <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>live · combined call revenue + brand inflow ({granWord})</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel}</span>
            </div>
            <AreaChart data={trend} format={formatINR} summaryLabel={trend[trend.length - 1]?.label} summaryValue={formatINR(latest)} summaryDelta={(delta >= 0 ? "+" : "") + delta.toFixed(1) + "% vs prev"} />
          </div>
        );
      })()}

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Wallets</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {walletTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {/* Creator wallet detail — per-pot Earned / Withdrawn / Available so the
          user sees the windowed flow per pot, not just current balance. */}
      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
        Creator wallet detail · per pot · {word}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        <Tile
          label="Calling pot" icon="$" variant="muted-lime"
          value={formatINR(cCalling.available)}
          subValue="call earnings · earned − withdrawn = avail"
          title={`Calling wallet (${word} flows, all creators; available is current):\n  Earned (call earnings):  ${fullINR(cCalling.earned)}\n  Withdrawn (payouts):    ${fullINR(cCalling.withdrawn)}\n  Available now:          ${fullINR(cCalling.available)}`}
          breakdown={[
            { label: "Earned",    value: formatINR(cCalling.earned) },
            { label: "Withdrawn", value: formatINR(cCalling.withdrawn) },
            { label: "Avail",     value: formatINR(cCalling.available) },
          ]}
        />
        <Tile
          label="Brand pot" icon="$" variant="muted-violet"
          value={formatINR(cBrand.available)}
          subValue="brand-campaign earnings · earned − withdrawn = avail"
          title={`Brand wallet (${word} flows, all creators; available is current):\n  Earned (campaigns):    ${fullINR(cBrand.earned)}\n  Withdrawn (payouts):   ${fullINR(cBrand.withdrawn)}\n  Available now:         ${fullINR(cBrand.available)}`}
          breakdown={[
            { label: "Earned",    value: formatINR(cBrand.earned) },
            { label: "Withdrawn", value: formatINR(cBrand.withdrawn) },
            { label: "Avail",     value: formatINR(cBrand.available) },
          ]}
        />
        <Tile
          label="Referral pot" icon="$" variant="muted-orange"
          value={formatINR(cReferral.available)}
          subValue="creator referral wallet · real ledger"
          title={
            `Referral wallet — REAL ledger from transaction_history:\n` +
            `  Earned     (yt-referral-reward):    ${fullINR(cReferral.earned)}\n` +
            `  Withdrawn  (youtuber-withdraw):     ${fullINR(cReferral.withdrawn)}\n` +
            `  Available  (wallets.referral_amount):${fullINR(cReferral.available)}\n` +
            `  ─────────────────\n` +
            `  Unaccounted (Earned − Withdrawn − Avail): ${fullINR(cReferral.unaccounted)}\n` +
            `  (Admin adjustments / in-flight withdrawals / rounding)\n\n` +
            `  Sanity check — rule-based reconstruction (PRD trigger gates):\n` +
            `    Creator → Fan (signup-gated):  ${formatNum(cReferral.creatorToFanCount)} × ₹50\n` +
            `    Creator → Creator (verified):  ${formatNum(cReferral.creatorToCreatorVerifiedCount)} × ₹750 + ₹250\n` +
            `    Creator → Creator (pending verification, no payout): ${formatNum(cReferral.creatorToCreatorPendingCount)}\n` +
            `    Rule-based earned (verified only): ${fullINR(cReferral.earnedByRules)}\n` +
            `    Δ vs ledger:         ${fullINR((cReferral.earned || 0) - (cReferral.earnedByRules || 0))}\n\n` +
            `  Excludes Fan→Fan referrals — those credit fan's regular wallet AFTER\n` +
            `  the new fan completes a successful call (call-completion gated).`
          }
          breakdown={[
            { label: "Earned",    value: formatINR(cReferral.earned) },
            { label: "Withdrawn", value: formatINR(cReferral.withdrawn) },
            { label: "Avail",     value: formatINR(cReferral.available) },
            ...(Math.abs(cReferral.unaccounted || 0) >= 1
              ? [{ label: "Unacc.", value: formatINR(cReferral.unaccounted) }]
              : []),
          ]}
        />
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Operations</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {operationsTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {/* Cashfree settlement reconciliation — null when creds missing or
          window outside reach. Hides cleanly. */}
      {data.cashfree && (
        <>
          <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>
            Cashfree settlement · {word}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
            <Tile label="Settled (Cashfree)" icon="C" variant="muted-lime"
              value={formatINR(data.cashfree.totalSettled)}
              subValue={`${formatNum(data.cashfree.paidCount)} settlements · ${formatNum(data.cashfree.uniqueUtrs)} UTRs`}
              title={`Cashfree gross settled in window:\n  Total settled:   ${fullINR(data.cashfree.totalSettled)}\n  Gross payment:   ${fullINR(data.cashfree.grossPayment)}\n  Settlements:     ${formatNum(data.cashfree.paidCount)} paid · ${formatNum(data.cashfree.pendingCount)} pending\n  Unique UTRs:     ${formatNum(data.cashfree.uniqueUtrs)}`}
            />
            <Tile label="Cashfree fees" icon="F" variant="muted-magenta"
              value={formatINR(data.cashfree.cashfreeFees)}
              subValue={`${(data.cashfree.effectiveFeePct || 0).toFixed(2)}% of gross payment`}
              title={`Cashfree gateway fees + tax in window:\n  Total fees: ${fullINR(data.cashfree.cashfreeFees)}\n  Effective rate: ${(data.cashfree.effectiveFeePct || 0).toFixed(3)}% of gross payment\n  (Reduces net settled INR vs gross)`}
            />
            <Tile label="Avg settlement lag" icon="L" variant="muted-violet"
              value={data.cashfree.avgLagHours > 0 ? `${data.cashfree.avgLagHours.toFixed(1)}h` : "—"}
              subValue="payment_till → settlement_date"
              title={`Average gap between Cashfree's payment_till\nand settlement_date in window: ${data.cashfree.avgLagHours.toFixed(1)} hours`}
            />
          </div>
        </>
      )}
      <PageGuide groups={MONEY_GUIDE} />
    </>
  );
}

// Definitions for every Money-page card. All sums NaN-guarded + test accounts
// excluded. "Available" balances are LIVE snapshots, not window-scoped.
const MONEY_GUIDE = [
  {
    section: "Inflow",
    rows: [
      { card: "Total inflow · window", means: "Call revenue (fan calls/rooms) + brand wallet deposits, in the period (incl. 18% GST). Hover to see the split.", rule: "transaction_history (fan calls/rooms, paid, not refund/top-up) + brand_transaction_history (wallet_deposit, credited)." },
      { card: "Transactions · window", means: "Count of successful payments (fan + brand) in the period.", rule: "Same two sources, counted." },
      { card: "Net (after GST) · window", means: "Total inflow with 18% GST removed.", rule: "inflow ÷ 1.18 — derived; GST is not stored separately." },
      { card: "Avg per day · window", means: "Average daily inflow across the period.", rule: "inflow ÷ number of days in the window." },
      { card: "Total inflow trend", means: "Call revenue + brand inflow over time (auto granularity).", rule: "Same two sources, bucketed per period." },
    ],
  },
  {
    section: "Wallets (live balances)",
    rows: [
      { card: "Fan / Creator / Brand wallets", means: "Money currently sitting in each group's wallets — a liability you hold.", rule: "SUM of wallets (fan=balance; creator=balance+referral+campaign pots) + brand_wallet. Live snapshot." },
      { card: "Total liability", means: "All wallet balances added together.", rule: "fan + creator + brand wallet balances." },
    ],
  },
  {
    section: "Money flow (window)",
    rows: [
      { card: "Fan flow", means: "How fan money moved — real money in, admin/promo credits, paid to creators, current balance, and a residual that closes the equation.", rule: "transaction_history split by payment_method + is_add_credit/is_wallet_credit_used. 'Available' = live wallets; 'Expired' = derived residual." },
      { card: "Brand flow", means: "Brand deposits (online vs admin), spend on creators, and current balance.", rule: "brand_transaction_history (deposits vs payouts) + live brand_wallet." },
      { card: "Creator pots", means: "Each creator pot (Calling / Brand / Referral) — earned, withdrawn and current balance.", rule: "earnings / campaign_enrollments / transaction_history referral ledger; withdraw requests; live wallet pots." },
    ],
  },
  {
    section: "Operations & gateway",
    rows: [
      { card: "Pending payouts · window", means: "Withdrawal requests queued in the period (creator + brand).", rule: "withdraw_requests + brand_withdraw_requests, status='pending', in window." },
      { card: "GST · window", means: "GST collected (derived, 18% inclusive).", rule: "revenue × 18/118 — a derived value, not recorded in the data." },
      { card: "Refunds · window", means: "Approved fan refunds in the period.", rule: "refund_requests, refund_status='approved', in window." },
      { card: "Cashfree settlements", means: "Money settled by the payment gateway, fees, effective fee %, and settlement lag.", rule: "External Cashfree settlements API (cached ~10 min) — not the database." },
    ],
  },
];
